import { useLocation, Link } from "react-router-dom";
import { LogoAvatar } from "@/components/shared/LogoAvatar";
import {
  LayoutDashboard,
  Building2,
  Map,
  UserSquare,
  ClipboardCheck,
  ClipboardEdit,
  Users,
  CalendarDays,
  CreditCard,
  ArrowUpCircle,
  TrendingUp,
  Star,
  Sparkles,
  Bell,
  Settings,
  ShieldCheck,
  Sliders,
  Palette,
  UsersRound,
  LogOut,
  X,
  LucideIcon,
  Pill,
  FileText,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { useTheme } from "@/contexts/ThemeContext";
import { RbacModule } from "@/rbac/modules";

interface NavItem {
  label: string;
  icon: LucideIcon;
  path: string;
  badge?: number;
  module: RbacModule;
  alwaysVisible?: boolean;
}

interface AppSidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const navItems: NavItem[] = [
  {
    label: "Dashboard",
    icon: LayoutDashboard,
    path: "/",
    module: "dashboard",
    alwaysVisible: true,
  },
  {
    label: "Businesses",
    icon: Building2,
    path: "/businesses",
    module: "businesses",
  },
  {
    label: "Eagle View Map",
    icon: Map,
    path: "/locations",
    module: "businesses",
  },
  { label: "Owners", icon: UserSquare, path: "/owners", module: "owners" },
  {
    label: "Approvals",
    icon: ClipboardCheck,
    path: "/approvals",
    module: "approvals",
  },
  {
    label: "Edit requests",
    icon: ClipboardEdit,
    path: "/edit-requests",
    module: "businesses",
  },
  { label: "Customers", icon: Users, path: "/customers", module: "customers" },
  {
    label: "Bookings",
    icon: CalendarDays,
    path: "/bookings",
    module: "bookings",
  },
  {
    label: "Subscriptions",
    icon: CreditCard,
    path: "/subscriptions",
    module: "subscriptions",
  },
  {
    label: "Invoices",
    icon: FileText,
    path: "/invoices",
    module: "subscriptions",
  },
  {
    label: "Upgrade requests",
    icon: ArrowUpCircle,
    path: "/plan-change-requests",
    module: "subscriptions",
  },
  { label: "Revenue", icon: TrendingUp, path: "/revenue", module: "revenue" },
  { label: "Featured", icon: Sparkles, path: "/featured", module: "featured" },
  { label: "Reviews", icon: Star, path: "/reviews", module: "reviews" },
  {
    label: "Notifications",
    icon: Bell,
    path: "/notifications",
    module: "notifications",
  },
  {
    label: "Plan Settings",
    icon: Settings,
    path: "/plan-settings",
    module: "planSettings",
  },
  {
    label: "Staff",
    icon: UsersRound,
    path: "/staff",
    module: "staffManagement",
  },
  {
    label: "Roles",
    icon: ShieldCheck,
    path: "/roles",
    module: "staffManagement",
  },
  {
    label: "Platform Settings",
    icon: Sliders,
    path: "/platform-settings",
    module: "platformSettings",
  },
  {
    label: "Medicines Directory",
    icon: Pill,
    path: "/medicines",
    module: "platformSettings",
  },
  { label: "Branding", icon: Palette, path: "/branding", module: "branding" },
];

export const AppSidebar = ({ isOpen, onClose }: AppSidebarProps) => {
  const location = useLocation();
  const { user, logout, can } = useAuth();
  const { branding } = useBranding();
  const { theme } = useTheme();
  const activeLogo =
    theme === "dark"
      ? branding?.logoDarkUrl || branding?.logoUrl
      : branding?.logoUrl;

  const visibleNavItems = navItems.filter(
    (item) => item.alwaysVisible || can(item.module, "view"),
  );

  const initials = user?.email
    ? user.email
        .split("@")[0]
        .split(/[._-]/)
        .map((part) => part[0]?.toUpperCase())
        .slice(0, 2)
        .join("")
    : "A";

  return (
    <aside
      className={`fixed left-0 top-0 bottom-0 w-60 bg-sidebar border-r border-sidebar-border flex flex-col z-30 transition-transform duration-200 ease-in-out ${
        isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
      }`}
    >
      <div className="p-5 flex items-center gap-3 border-b border-sidebar-border">
        <LogoAvatar
          src={activeLogo}
          name={branding?.appName ?? "Ammayum Kunjum"}
          size="sm"
          className="w-9 h-9 text-sm font-bold rounded-lg shrink-0"
        />
        <span className="font-semibold text-sidebar-foreground text-sm flex-1 truncate">
          {branding?.appName ?? "Ammayum Kunjum"}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="md:hidden p-1 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
        {visibleNavItems.map((item) => {
          const isActive =
            location.pathname === item.path ||
            (item.path !== "/" && location.pathname.startsWith(item.path));
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-btn text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              <item.icon className="w-[18px] h-[18px] shrink-0" />
              <span className="flex-1">{item.label}</span>
              {item.badge !== undefined && (
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                    isActive
                      ? "bg-primary-foreground/20 text-primary-foreground"
                      : "bg-warning text-warning-foreground"
                  }`}
                >
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-sidebar-border flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-primary-light flex items-center justify-center shrink-0">
          <span className="text-primary-dark text-xs font-semibold">
            {initials}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-sidebar-foreground truncate">
            Admin
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {user?.email ?? ""}
          </p>
        </div>
        <button
          type="button"
          onClick={logout}
          className="text-muted-foreground hover:text-destructive transition-colors"
          aria-label="Log out"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </aside>
  );
};

export default AppSidebar;
